/*
Cart Pre-order synchronizer
- Ensures each cart line item has properties[_pre_order] set to "yes" or "no" per rules.
- If yes, also sets properties[Note] with the explanatory text. If no, removes it.
- Runs on cart page and cart drawer load/updates.

Rules per line item (using current variant state):
if variant.inventory_quantity <= 0 AND variant.inventory_policy == 'continue' AND variant.inventory_quantity > threshold
  _pre_order = "yes"
else
  _pre_order = "no"
Where threshold is metafields.custom.pre_order_inventory exposed in Liquid as window.preorderInventory[variantId].
*/
(function(){
  const NOTE_TEXT = 'Please note this is a pre-order item. The shipping window for this item is included on the product page.';

  async function fetchJSON(url, options) {
    const res = await fetch(url, options);
    if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
    return res.json();
  }

  async function getCart() {
    return fetchJSON('/cart.js');
  }

  const variantCache = new Map();
  async function getVariant(variantId) {
    if (variantCache.has(variantId)) return variantCache.get(variantId);
    const data = await fetchJSON(`/variants/${variantId}.json`);
    // Normalize useful fields from either storefront shapes:
    // - { variant: {...} }
    // - { product_variant: {...} }
    // - or the object itself
    const src = (data && (data.variant || data.product_variant)) || data || {};
    const v = {
      id: src?.id,
      inventory_quantity: Number(src?.inventory_quantity ?? 0),
      inventory_policy: String(src?.inventory_policy ?? '')
    };
    variantCache.set(variantId, v);
    return v;
  }

  function computePreorderFor(variant) {
    const thresholds = window.preorderInventory || {};
    const thresholdRaw = thresholds[String(variant.id)];
    const threshold = (typeof thresholdRaw === 'number') ? thresholdRaw : -999; // if unknown, treat as a very low threshold
    const qty = Number(variant.inventory_quantity);
    const policy = String(variant.inventory_policy);
    const isPre = (qty <= 0) && (policy === 'continue') && (qty > threshold);
    return isPre ? 'yes' : 'no';
  }

  function buildUpdatedProperties(existingProps, desiredPre) {
    const props = Object.assign({}, existingProps || {});
    // Always set _pre_order explicitly
    props._pre_order = desiredPre;
    // Maintain/clear Note
    if (desiredPre === 'yes') {
      props.Note = NOTE_TEXT;
    } else {
      if (props.Note !== undefined) props.Note = '';
    }
    return props;
  }

  async function updateLineItemProperties(item, newProperties) {
    // Use change.js with the line item key and properties; include quantity to be explicit
    const body = JSON.stringify({ id: item.key, quantity: item.quantity, properties: newProperties });
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body
    });
    if (!res.ok) throw new Error(`Failed to update properties for ${item.key}`);
    return res.json();
  }

  function needsUpdate(item, desiredPre) {
    const currentPre = item?.properties?._pre_order;
    const currentNote = item?.properties?.Note;
    if (currentPre !== desiredPre) return true;
    if (desiredPre === 'yes') {
      return currentNote !== NOTE_TEXT;
    } else {
      // Note should be absent or empty
      return !!currentNote;
    }
  }

  // Update the Availability column UI immediately without waiting for a full section rerender
  function setAvailabilityForLine({ key, variantId, desiredPre }) {
    const candidates = document.querySelectorAll(
      `.cart-availability-text[data-cart-key="${CSS.escape(key)}"], .cart-availability-text[data-variant-id="${CSS.escape(String(variantId))}"]`
    );
    if (!candidates || candidates.length === 0) return;
    candidates.forEach(el => {
      el.classList.remove('preorder-note', 'available-note');
      if (desiredPre === 'yes') {
        el.classList.add('preorder-note');
        el.textContent = NOTE_TEXT;
        el.style.display = 'inline-block';
        el.style.maxWidth = '300px';
      } else {
        el.classList.add('available-note');
        el.textContent = 'Available';
        // remove inline styles if previously set
        el.style.removeProperty('display');
        el.style.removeProperty('max-width');
      }
    });
  }

  function refreshAvailabilityUI(desiredMap) {
    if (!desiredMap) return;
    desiredMap.forEach((val, key) => {
      setAvailabilityForLine({ key, variantId: val.variantId, desiredPre: val.desiredPre });
    });
  }

  function showWarningOnce() {
    if (document.querySelector('[data-preorder-warning]')) return;
    const warning = document.createElement('div');
    warning.setAttribute('data-preorder-warning', '');
    warning.textContent = 'Some items in your cart have changed availability status. Please review before completing checkout.';
    warning.style.background = '#fff3cd';
    warning.style.border = '1px solid #ffeeba';
    warning.style.color = '#856404';
    warning.style.padding = '10px 12px';
    warning.style.margin = '10px 0';
    warning.style.fontSize = '14px';
    warning.style.borderRadius = '4px';

    // Prefer placing inside cart containers if present
    const cartContainer = document.getElementById('main-cart-items') || document.getElementById('CartDrawer');
    if (cartContainer) {
      cartContainer.prepend(warning);
    } else {
      document.body.prepend(warning);
    }
  }

  async function sync() {
    try {
      const cart = await getCart();
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return false;

      // Ensure we have thresholds for current items; if not, we still compute with default threshold
      const updates = [];
      let statusChanged = false; // track if any _pre_order status flips

      // Fetch all needed variant data in parallel (unique variants only)
      const uniqueVariantIds = Array.from(new Set(cart.items.map(i => i.variant_id)));
      await Promise.all(uniqueVariantIds.map(id => getVariant(id)));

      const desiredMap = new Map();
      for (const item of cart.items) {
        const variant = await getVariant(item.variant_id);
        const desiredPre = computePreorderFor(variant);
        desiredMap.set(item.key, { desiredPre, variantId: item.variant_id });

        // Determine if preorder status changed for this item
        const currentPre = item?.properties?._pre_order;
        if (currentPre !== desiredPre) {
          statusChanged = true;
        }

        if (needsUpdate(item, desiredPre)) {
          const newProps = buildUpdatedProperties(item.properties, desiredPre);
          updates.push({ item, newProps });
        }
      }

      // Update Availability column UI immediately regardless of whether properties need updating
      refreshAvailabilityUI(desiredMap);

      if (updates.length === 0) return false;

      // Apply updates sequentially to avoid race conditions rewriting cart
      for (const u of updates) {
        await updateLineItemProperties(u.item, u.newProps);
      }

      // Only show the warning if any item's preorder status actually changed
      if (statusChanged) {
        showWarningOnce();
      }
      // After updates, sections will not auto-refresh. Trigger a soft refresh if cart sections exist
      // Attempt to re-render via the existing cart JS if present
      const cartItemsEl = document.querySelector('cart-items, cart-drawer-items');
      if (cartItemsEl && typeof cartItemsEl.getSectionsToRender === 'function') {
        try {
          const body = JSON.stringify({
            sections: (cartItemsEl.getSectionsToRender && cartItemsEl.getSectionsToRender().map(s => s.section)) || [],
            sections_url: window.location.pathname
          });
          const res = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
          if (res.ok) {
            // nothing else, cart.js will not replace automatically here; the next cart interaction will refresh
          }
        } catch (_e) {}
      }
      return true;
    } catch (e) {
      console && console.warn && console.warn('Pre-order sync failed', e);
      return false;
    }
  }

  // Expose API
  window.CartPreorder = {
    sync,
    computePreorderFor
  };

  // Auto-run on load for cart contexts
  document.addEventListener('DOMContentLoaded', () => {
    // Only run on pages where cart elements or cart drawer exist
    if (document.querySelector('#main-cart-items') || document.querySelector('#CartDrawer')) {
      // yield to allow other JS to finish first
      setTimeout(() => { window.CartPreorder.sync() }, 150);
    }
  });
})();
